import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Volume2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { lockBodyScroll } from "@/lib/bodyScrollLock";
import { useApp } from "@/context/AppContext";
import { useUploadQueue } from "@/context/UploadQueueContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { gradientCss } from "@/lib/gradients";
import { mediaTransformStyle, StoryBlurBackdrop, storyBlurSrc, attachLocalPoster, revokeMediaUrls, LINK_STICKER_DEFAULT, VIEWER_SAFE_TOP, VIEWER_SAFE_BOTTOM } from "@/components/StoryMedia";
import { MusicPicker } from "@/components/MusicPicker";
import { UploadOverlay } from "@/components/PostComposer";
import { parseVideoEmbed, parseFirstUrl } from "@/lib/embeds";
import { pushBackHandler } from "@/lib/backButton";
import { prepareStoryMediaUrl, uploadStoryDrawing, trimPayload } from "@/lib/storyPublish";
import { StoryTopBar, StoryBottomBar, TrashZone } from "./StoryEditorBars";
import { StoryChipRow, GalleryRow } from "./StoryChipRow";
import { StoryMediaLayer } from "./StoryMediaLayer";
import { StoryDrawLayer } from "./StoryDrawLayer";
import { StoryDrawTools } from "./StoryDrawTools";
import { StoryStickerLayer } from "./StoryStickerLayer";
import { StoryTextTool } from "./StoryTextTool";
import { StoryStickerSheet } from "./StoryStickerSheet";
import { StoryShareSheet } from "./StoryShareSheet";
import { StoryTrimSheet } from "./StoryTrimSheet";
import { BottomSheet, MentionSheet, LocationSheet, LinkSheet, ChurchPickSheet, MoreSheet, ScheduleSheet, CloseSheet, SaveTemplateSheet } from "./StorySheets";
import { StoryTemplatesRow } from "./StoryTemplatesRow";
import { StoryPreview } from "./StoryPreview";
import { STORY_TEMPLATES, buildTemplateStickers, buildChurchServiceTemplate } from "./storyTemplates";
import { weekdayNames } from "@/lib/denominations";
import { newId, DRAFT_KEY_FS, IDENTITY_TF, bgStyleOf, editorStateFromStory, verseText } from "./storyEditorUtils";
import { MediaFxEditor } from "@/components/media/MediaFxEditor";
import { StoryCamera } from "./StoryCamera";
import { hasFx } from "@/lib/mediaFx";
import { readDefaultStoryAudience, writeDefaultStoryAudience } from "@/lib/postAudience";
import { StoryHiddenFromSheet } from "./StoryHiddenFrom";
import { CloseFriendsSheet } from "@/components/CloseFriends";

// Éditeur de story plein écran façon Instagram — téléphone (web) et app native uniquement.
// Le PC garde la feuille à deux colonnes (StoryComposer).
export const StoryFullscreenEditor = (props) => (props.open ? <Editor {...props} /> : null);

const Editor = ({ onOpenChange, onCreated, initialChurchId, initialAsChurch = false, editStory = null }) => {
  const { t, config, user, lang } = useApp();
  // Mode édition : annonce d'église déjà programmée (ouverte depuis la page Église) → on enregistre au lieu de publier.
  const editMode = !!editStory;
  const init = useMemo(() => (editStory ? editorStateFromStory(editStory) : null), [editStory]);
  const { enqueueStory } = useUploadQueue();
  const frameRef = useRef(null);
  const drawRef = useRef(null);
  const trashRef = useRef(null);
  const safeTopRef = useRef(null);
  const safeBottomRef = useRef(null);
  const safeRefs = useMemo(() => ({ top: safeTopRef, bottom: safeBottomRef }), []);
  const videoRef = useRef(null);

  const [stage, setStage] = useState(init?.stage || "text"); // text (fond coloré) | media (photo / vidéo)
  const [media, setMedia] = useState(init?.media || null);
  const [bg, setBg] = useState(init?.bg || "navy");
  const [transform, setTransform] = useState(init?.transform || IDENTITY_TF);
  const [stickers, setStickers] = useState(init?.stickers || []);
  const [selected, setSelected] = useState(null);
  const [strokes, setStrokes] = useState([]);
  const [baseDrawing, setBaseDrawing] = useState(init?.drawingUrl || null); // dessin déjà enregistré (édition)
  const [tool, setTool] = useState(null); // text | draw | stickers | music | trim | more | schedule | share | hidden | close_friends | church | close | location | mention
  const [editingText, setEditingText] = useState(null);
  const [draw, setDraw] = useState({ tool: "pen", color: "#FFFFFF", thickness: 0.35 });
  const [music, setMusic] = useState(null);
  const [musicVolume, setMusicVolume] = useState(0.7);
  const [mentions, setMentions] = useState([]);
  const [scheduledAt, setScheduledAt] = useState(init?.scheduledAt || "");
  const [busy, setBusy] = useState(false);
  const [proc, setProc] = useState(null);
  const [drag, setDrag] = useState({ dragging: false, overTrash: false });
  // Carte de lien : URL détectée dans les bulles OU collée via le sticker « Lien » (manualLink, prioritaire).
  const [linkEmbed, setLinkEmbed] = useState(init?.embed?.url ? init.embed : null);
  const [manualLink, setManualLink] = useState(init?.embed?.url || null);
  const [linkNonce, setLinkNonce] = useState(0);
  const dismissedLinkRef = useRef(new Set());
  const fetchedLinkRef = useRef(init?.embed?.url || null);
  const [previewDrawing, setPreviewDrawing] = useState(null); // PNG (blob) du calque pour l'aperçu
  const verseCache = useRef(null);
  const [churchTemplates, setChurchTemplates] = useState([]); // modèles enregistrés par mes églises
  const [elementsChurchId, setElementsChurchId] = useState(null); // église des « éléments » de la feuille Stickers
  // Vidéo : durée réelle, début / longueur de l'extrait, qualité du clip
  const [videoDur, setVideoDur] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimLenState, setTrimLenState] = useState(null);
  const [clipQuality, setClipQuality] = useState("standard");
  const maxSec = Number(config?.settings?.story_video_max_seconds) || 30;
  const storyVideoMaxMb = Number(config?.settings?.media_limits?.story_video_max_mb) || Number(config?.settings?.media_limits?.video_max_mb) || 100;
  const trimLen = trimLenState ?? Math.min(maxSec, videoDur || maxSec);
  const setTrim = ({ start, len }) => { setTrimStart(start); setTrimLenState(len); };
  // Découpe seulement pour un fichier local (une vidéo déjà envoyée — édition — est reprise telle quelle).
  const canTrim = media?.media_type === "video" && !!media.file && videoDur >= 2;
  const needsTrim = media?.media_type === "video" && !!media.file && videoDur > maxSec + 0.5; // trop longue : découpe obligatoire
  // Lot 613 — extrait réellement découpé : trop longue, ou début/fin déplacés par l'utilisateur (même sous la limite).
  const willTrim = canTrim && (needsTrim || trimStart > 0.05 || trimLen < videoDur - 0.3);
  const willProcess = media?.media_type === "video" && (willTrim || (!!media?.file && media.file.size > storyVideoMaxMb * 1024 * 1024));
  const trimOpts = { videoDur, maxSec, storyVideoMaxMb, needsTrim: willTrim, trimStart, trimLen, clipQuality };

  // Story d'église : mêmes règles que la feuille PC (GET /stories/church-eligibility, contrôle serveur à la publication).
  const [churchElig, setChurchElig] = useState({ can: false, mode: null, churches: [] });
  const churches = churchElig.churches || [];
  const feat = config?.settings?.features || {};
  const mandatory = feat.preacher_publish_as !== false && churchElig.mode === "preacher" && churches.length > 0;
  // Mémoire de la dernière destination : réservée aux prédicateurs (comme le bloc « Publier en tant que » du PC).
  const rememberDest = feat.publish_as_memory !== false && churchElig.mode === "preacher";
  const memKey = user ? `yalola.publish_as.${user.id}` : null;
  const [dest, setDest] = useState(null);
  // Lot 651 — audience de « Ma story » (mémoire séparée des publications) ; une story d'église reste publique.
  const [audience, setAudienceState] = useState(() => readDefaultStoryAudience(user));
  const setAudience = (v) => { setAudienceState(v); writeDefaultStoryAudience(v); };
  useEffect(() => {
    api.get("/stories/church-eligibility")
      .then(({ data }) => {
        const elig = data || { can: false, churches: [] };
        setChurchElig(elig);
        const list = elig.churches || [];
        if (initialAsChurch && initialChurchId && list.some((c) => c.id === initialChurchId)) { setDest(initialChurchId); return; }
        let saved = null;
        try { saved = elig.mode === "preacher" && memKey ? localStorage.getItem(memKey) : null; } catch { /* noop */ }
        if (saved === "me" || list.some((c) => c.id === saved)) setDest(saved);
        else if (!(elig.mode === "preacher" && list.length)) setDest("me");
      })
      .catch(() => setChurchElig({ can: false, mode: null, churches: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!churches.length) return;
    api.get("/stories/church-templates").then(({ data }) => setChurchTemplates(data?.templates || [])).catch(() => setChurchTemplates([]));
  }, [churches.length]);

  // Écran verrouillé + bouton retour Android : ferme d'abord l'outil ouvert, puis l'éditeur.
  const backRef = useRef(() => false);
  useEffect(() => {
    const unlock = lockBodyScroll();
    let off = null;
    const id = setTimeout(() => { off = pushBackHandler(() => backRef.current()); }, 0);
    return () => { unlock(); clearTimeout(id); off?.(); };
  }, []);

  // Brouillon : restauré à l'ouverture, enregistré à chaque changement (média local non persistable).
  // Pas de brouillon en mode édition.
  useEffect(() => {
    if (editMode) return;
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY_FS) || "null");
      if (!d) return;
      if (d.bg) setBg(d.bg);
      if (Array.isArray(d.stickers)) setStickers(d.stickers);
      if (Array.isArray(d.strokes)) setStrokes(d.strokes);
      if (d.link_url) setManualLink(d.link_url);
      if (d.media?.url) {
        setStage("media");
        setMedia({ file: null, localUrl: null, url: d.media.url, media_type: d.media.media_type, progress: 100, status: "done" });
        if (d.transform) setTransform(d.transform);
      }
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    if (editMode) return;
    const m = media?.status === "done" && media.url ? { url: media.url, media_type: media.media_type } : null;
    if (!stickers.length && !strokes.length && !m) { localStorage.removeItem(DRAFT_KEY_FS); return; }
    try { localStorage.setItem(DRAFT_KEY_FS, JSON.stringify({ bg, stickers, strokes: strokes.slice(-80), media: m, transform, link_url: manualLink || undefined })); } catch { /* noop */ }
  }, [bg, stickers, strokes, media, transform, manualLink, editMode]);

  // Aperçu de lien : première URL trouvée dans les bulles, ou lien collé via le sticker « Lien » — sur tous les
  // types de story (fond coloré, photo, vidéo).
  const joinedText = useMemo(() => stickers.filter((s) => s.type === "text").map((s) => s.text).join("\n").trim(), [stickers]);
  const textLinkUrl = useMemo(() => parseVideoEmbed(joinedText)?.url || parseFirstUrl(joinedText) || null, [joinedText]);
  const rawLinkUrl = manualLink || textLinkUrl;
  useEffect(() => {
    if (!rawLinkUrl) { setLinkEmbed(null); fetchedLinkRef.current = null; return undefined; }
    if (dismissedLinkRef.current.has(rawLinkUrl) || fetchedLinkRef.current === rawLinkUrl) return undefined;
    const timer = setTimeout(async () => {
      fetchedLinkRef.current = rawLinkUrl;
      try { const { data } = await api.get("/link-preview", { params: { url: rawLinkUrl } }); setLinkEmbed(data.preview || null); }
      catch {
        const video = parseVideoEmbed(rawLinkUrl);
        let domain = ""; try { domain = new URL(rawLinkUrl).hostname.replace(/^www\./, ""); } catch { /* noop */ }
        // Lien collé à la main : la carte s'affiche même sans aperçu (URL + domaine) ; lien du texte : vidéo seulement.
        setLinkEmbed(video ? { ...video, title: null, author: null, thumbnail: null } : (manualLink ? { url: rawLinkUrl, kind: "article", domain } : null));
      }
    }, manualLink ? 0 : 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawLinkUrl, linkNonce]);

  // Carte de lien = sticker déplaçable (façon Instagram) : ajoutée dès que l'aperçu arrive, retirée quand le lien
  // disparaît (URL effacée, carte refermée). La corbeille / la croix sur le sticker referment la carte.
  useEffect(() => {
    setStickers((arr) => {
      const has = arr.some((s) => s.type === "link");
      if (linkEmbed && !has) return [...arr, { id: newId(), type: "link", ...LINK_STICKER_DEFAULT }].slice(-20);
      if (!linkEmbed && has) return arr.filter((s) => s.type !== "link");
      return arr;
    });
  }, [linkEmbed]);
  const dismissLink = () => {
    if (rawLinkUrl) dismissedLinkRef.current.add(rawLinkUrl);
    if (textLinkUrl) dismissedLinkRef.current.add(textLinkUrl);
    setManualLink(null);
    setLinkEmbed(null);
  };
  const addManualLink = (url) => {
    dismissedLinkRef.current.delete(url);
    fetchedLinkRef.current = null;
    setManualLink(url);
    setLinkNonce((n) => n + 1);
    closeTool();
  };

  // Vidéo plus longue que la limite → la feuille « Extrait » s'ouvre d'elle-même.
  useEffect(() => { if (needsTrim) setTool("trim"); }, [needsTrim]);
  useEffect(() => { if (willTrim && videoRef.current) { try { videoRef.current.currentTime = trimStart; } catch { /* noop */ } } }, [trimStart, trimLen, willTrim]);

  const hasContent = stage === "media" ? !!media : (stickers.length > 0 || strokes.length > 0 || !!baseDrawing);
  const closeTool = () => { setTool(null); setEditingText(null); };
  const requestClose = () => { if (hasContent || editMode) setTool("close"); else onOpenChange(false); };
  backRef.current = () => { if (tool) closeTool(); else requestClose(); return true; };

  const handleFiles = (files) => {
    const f = files && files[0];
    if (!f) return;
    revokeMediaUrls(media);
    const isVideo = f.type.startsWith("video");
    const localUrl = URL.createObjectURL(f);
    setMedia({ file: f, localUrl, url: null, media_type: isVideo ? "video" : "image", progress: 0, status: isVideo ? "local" : "done" });
    if (isVideo) attachLocalPoster(localUrl, f, setMedia);
    setStage("media"); setTransform(IDENTITY_TF); setVideoDur(0); setTrimStart(0); setTrimLenState(null); setClipQuality("standard"); closeTool();
  };
  const removeMedia = () => {
    revokeMediaUrls(media);
    setMedia(null); setStage("text"); setTransform(IDENTITY_TF); setVideoDur(0); setTrimStart(0); setTrimLenState(null); closeTool();
  };
  // Lot 534 — éditeur Filtres / Réglages / Recadrer : photo → nouveau fichier « cuit » (envoyé tel quel) ; vidéo → fx dans
  // media_transform.fx, appliqué en CSS par l'éditeur, l'aperçu, la visionneuse et la vignette.
  // Lot 535 — caméra intégrée : photo déjà filtrée (fichier) ; vidéo brute + fx CSS (miroir selfie compris).
  const onCameraCapture = ({ file, fx, duration }) => {
    closeTool();
    handleFiles([file]);
    if (fx) setTransform({ ...IDENTITY_TF, fx });
    if (duration > 0) setVideoDur(duration);
  };
  const applyFx = (res) => {
    closeTool();
    if (!res) return;
    if (res.file) {
      revokeMediaUrls(media);
      setMedia({ file: res.file, localUrl: URL.createObjectURL(res.file), url: null, media_type: "image", progress: 100, status: "done" });
      setTransform(IDENTITY_TF);
    } else if ("fx" in res) {
      setTransform((tf) => { const { fx: _old, ...rest } = tf; return res.fx ? { ...rest, fx: res.fx } : rest; });
    }
  };
  // Durée infinie (WebM filmé dans l'app) → on garde la durée mesurée par la caméra. Un simple rechargement des
  // métadonnées (même durée) ne réinitialise PAS l'extrait choisi.
  const onPreviewMeta = (e) => { const d = e.currentTarget.duration; if (d && isFinite(d) && Math.abs(d - videoDur) > 0.05) { setVideoDur(d); setTrimStart(0); setTrimLenState(null); } };
  const onPreviewTime = (e) => {
    if (!willTrim) return;
    const v = e.currentTarget;
    if (v.currentTime > trimStart + trimLen || v.currentTime < trimStart - 0.5) { try { v.currentTime = trimStart; } catch { /* noop */ } }
  };

  // Stickers
  const place = () => { const n = stickers.length % 5; return { x: 0.5, y: 0.28 + n * 0.11 }; };
  const addSticker = (partial) => {
    const id = newId();
    setStickers((arr) => [...arr, { id, rotation: 0, ...place(), ...partial }].slice(-20));
    setSelected(id);
    closeTool();
  };
  const onTextDone = (fields) => {
    // Lot 713 — « texte long » : une seule bulle de ce type, centrée (fitBubble l'ajuste au cadre), les autres stickers restent.
    const centered = fields.long ? { x: 0.5, y: 0.5, rotation: 0 } : {};
    if (editingText) setStickers((arr) => arr.filter((s) => s.id === editingText.id || !fields.long || !s.long).map((s) => (s.id === editingText.id ? { ...s, ...fields, ...centered } : s)));
    else if (fields.long) { const id = newId(); setStickers((arr) => [...arr.filter((s) => !s.long), { id, type: "text", rotation: 0, x: 0.5, y: 0.5, ...fields }].slice(-20)); setSelected(id); }
    else addSticker({ type: "text", ...fields });
    closeTool();
  };
  const addMention = (u) => {
    setMentions((arr) => (arr.some((m) => m.id === u.id) ? arr : [...arr, { id: u.id, name: u.name }].slice(0, 10)));
    addSticker({ type: "mention", text: `@${u.name}`, user_id: u.id, scale: 0.055 });
  };

  // Publication — « Ma story » / « Église » publient aussitôt ; la flèche passe par la feuille Partager.
  const finish = (created) => { localStorage.removeItem(DRAFT_KEY_FS); onOpenChange(false); if (created) onCreated?.(); };
  const publishTo = async (target) => {
    if (busy) return;
    if (!hasContent) { toast.info(t("story.empty_hint", "Ajoute un texte, une photo ou un dessin pour partager.")); return; }
    if (mandatory && !target) { setTool("share"); return; }
    if (stage === "media" && media?.status === "error") return;
    const church = target && target !== "me" ? { church_id: target } : {};
    if (rememberDest && memKey && target) { try { localStorage.setItem(memKey, target); } catch { /* noop */ } }
    setBusy(true);
    try {
      const extras = {};
      if (music) { extras.music_url = music.url; extras.music_title = music.title; extras.music_volume = musicVolume; if (music.start) extras.music_start = music.start; }
      if (mentions.length) extras.mentions = mentions;
      if (!church.church_id) extras.visibility = audience; // lot 651 — audience (story personnelle seulement)
      if (scheduledAt) { try { extras.scheduled_at = new Date(scheduledAt).toISOString(); } catch { /* noop */ } }
      extras.stickers_safe = true; // lot 664 : stickers déjà tenus hors de l'en-tête / barre du bas → la visionneuse ne remappe pas
      const drawing_url = await resolveDrawingUrl();
      if (drawing_url) extras.drawing_url = drawing_url;
      if (stage === "text") {
        const res = await api.post("/stories", { media_type: "text", text: joinedText, bg, layout: "free", stickers, ...(linkEmbed ? { embed: linkEmbed } : {}), ...church, ...extras });
        toast.success(res?.data?.scheduled ? t("story.scheduled_ok", "Story programmée ✓ — elle sera publiée automatiquement.") : t("story.created", "Story publiée !"));
        finish(!res?.data?.scheduled);
        return;
      }
      // Média (immédiat OU programmé : scheduled_at dans extras) → file d'attente en arrière-plan
      // (découpe + envoi + création), fermeture instantanée — plus de bouton figé sur une grosse vidéo.
      enqueueStory({
        mode: "media",
        media: { file: media.file, url: media.url, media_type: media.media_type, poster_url: media.poster_url || null },
        text: "", bg, transform, stickers, church, extras, embed: linkEmbed || null,
        trimOpts,
        previewFile: media.file || null,
        mediaKind: media.media_type,
      });
      finish(false);
    } catch (e) { setProc(null); toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const onChurchBtn = () => { if (churches.length === 1) publishTo(churches[0].id); else setTool("church"); };

  // Modèles prêts : fond + bulles + stickers déjà placés (annulable via le toast si la story avait déjà des stickers).
  const loadVerse = async () => {
    if (verseCache.current) return verseCache.current;
    try {
      const { data } = await api.get("/verses/today");
      const txt = verseText(data?.verse, lang);
      if (txt) verseCache.current = { text: txt, ref: data.verse.reference || "" };
    } catch { /* verset indisponible → texte de repli dans le modèle */ }
    return verseCache.current;
  };
  const applyTemplate = async (key) => {
    const tpl = STORY_TEMPLATES.find((x) => x.key === key);
    if (!tpl) return;
    const verse = key === "verse" ? await loadVerse() : null;
    const prev = { bg, stickers };
    if (stage === "text") setBg(tpl.bg);
    setStickers(buildTemplateStickers(key, { t, lang, city: user?.city || "", verse }).slice(0, 20));
    setSelected(null);
    if (tool === "templates") closeTool();
    if (prev.stickers.length) {
      // En bas pour ne pas recouvrir la barre d'outils du haut
      toast(t("story.tpl_applied", "Modèle appliqué"), { position: "bottom-center", action: { label: t("story.undo_template", "Annuler"), onClick: () => { setBg(prev.bg); setStickers(prev.stickers); } } });
    }
  };

  // Modèles d'église : « Culte de l'église » généré + modèles enregistrés ; appliquer présélectionne la destination Église.
  const applyChurchContent = (church, { bg: nbg, stickers: nst, media_url, media_transform, drawing_url }) => {
    const prev = { bg, stickers, stage, media, transform, baseDrawing, strokes };
    if (media_url) {
      setStage("media");
      setMedia({ file: null, localUrl: null, url: media_url, media_type: "image", progress: 100, status: "done" });
      setTransform(media_transform || IDENTITY_TF);
    } else {
      setStage("text");
      setBg(nbg || "navy");
    }
    setStickers((nst || []).map((st) => ({ ...st, id: newId() })).slice(0, 20));
    setStrokes([]);
    setBaseDrawing(drawing_url || null);
    setSelected(null);
    if (!editMode) setDest(church.id);
    if (tool === "templates") closeTool();
    if (prev.stickers.length || prev.strokes.length || prev.media) {
      toast(t("story.tpl_applied", "Modèle appliqué"), { position: "bottom-center", action: { label: t("story.undo_template", "Annuler"), onClick: () => { setStage(prev.stage); setMedia(prev.media); setTransform(prev.transform); setBg(prev.bg); setStickers(prev.stickers); setStrokes(prev.strokes); setBaseDrawing(prev.baseDrawing); } } });
    }
  };
  const applyChurchService = (church) => applyChurchContent(church, buildChurchServiceTemplate(church, { t, weekdays: weekdayNames(t) }));
  const applyChurchTemplate = (tpl) => {
    const church = churches.find((c) => c.id === tpl.church_id) || { id: tpl.church_id };
    applyChurchContent(church, tpl);
  };
  const deleteChurchTemplate = async (tpl) => {
    if (!window.confirm(t("story.tpl_delete_confirm", "Supprimer ce modèle ?"))) return;
    try {
      await api.delete(`/churches/${tpl.church_id}/story-templates/${tpl.id}`);
      setChurchTemplates((arr) => arr.filter((x) => x.id !== tpl.id));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  // Sections « Modèles de {église} » : églises avec des modèles enregistrés, l'église de destination, ou l'unique église autorisée.
  const churchSections = churches
    .filter((c) => churchTemplates.some((tp) => tp.church_id === c.id) || c.id === dest || churches.length === 1 || (editMode && c.id === (editStory.church_id || initialChurchId)))
    .slice(0, 6)
    .map((c) => ({ church: c, templates: churchTemplates.filter((tp) => tp.church_id === c.id) }));
  const saveAsTemplate = async ({ churchId, name }) => {
    if (stage === "media" && media?.media_type === "video") { toast.error(t("story.tpl_no_video", "Les modèles n'acceptent pas la vidéo (photo, fond, bulles, stickers, dessin).")); return; }
    if (!hasContent) { toast.info(t("story.empty_hint", "Ajoute un texte, une photo ou un dessin pour partager.")); return; }
    setBusy(true);
    try {
      const body = { name, bg, stickers, drawing_url: await resolveDrawingUrl() };
      if (stage === "media" && media) {
        const m = await prepareStoryMediaUrl({ media, config, t, ...trimOpts, setProc });
        body.media_url = m.url;
        body.media_transform = transform;
      }
      const { data } = await api.post(`/churches/${churchId}/story-templates`, body);
      if (data?.template) setChurchTemplates((arr) => [data.template, ...arr.filter((x) => x.id !== data.template.id)]);
      toast.success(t("story.tpl_saved", "Modèle enregistré ✓"), { position: "bottom-center" });
      closeTool();
    } catch (e) { setProc(null); toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };

  // Aperçu plein écran (rendu abonnés) : capture du calque de dessin en PNG le temps de l'aperçu.
  useEffect(() => {
    if (tool !== "preview") { setPreviewDrawing(null); return undefined; }
    let url = null, alive = true;
    (async () => {
      const blob = await drawRef.current?.exportPng?.();
      if (alive && blob) { url = URL.createObjectURL(blob); setPreviewDrawing(url); }
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [tool]);
  const openPreview = () => { setSelected(null); setTool("preview"); };
  const previewAuthor = (() => {
    const cid = editMode ? (editStory.church_id || initialChurchId) : (dest && dest !== "me" ? dest : null);
    if (cid) return { name: churches.find((c) => c.id === cid)?.name || editStory?.church_name || t("story.church_story", "Église"), is_church: true };
    return { name: user?.name, avatar_url: user?.avatar_url };
  })();
  const shareFromPreview = () => {
    if (editMode) { saveEdit(); return; }
    if (mandatory && !dest) { setTool("share"); return; }
    publishTo(dest || "me");
  };

  // Calque de dessin à enregistrer : nouveaux traits → PNG fusionné (base + traits) ; sinon dessin existant tel quel.
  const resolveDrawingUrl = async () => {
    if (!strokes.length) return baseDrawing || null;
    const url = await uploadStoryDrawing(drawRef.current?.exportPng, config).catch(() => null);
    if (!url) throw { friendlyMessage: t("story.drawing_upload_fail", "Le dessin n'a pas pu être envoyé. Réessaie.") };
    return url;
  };

  // Mode édition : enregistre l'annonce programmée (PUT) sans changer son statut.
  const saveEdit = async () => {
    if (busy) return;
    if (!hasContent) { toast.info(t("story.empty_hint", "Ajoute un texte, une photo ou un dessin pour partager.")); return; }
    let schedIso = null;
    try { schedIso = scheduledAt ? new Date(scheduledAt).toISOString() : null; } catch { /* noop */ }
    if (!schedIso || new Date(schedIso).getTime() <= Date.now()) { toast.error(t("story.schedule_required", "Choisis une date et une heure futures.")); setTool("schedule"); return; }
    const cid = editStory.church_id || initialChurchId;
    setBusy(true);
    try {
      const drawing_url = await resolveDrawingUrl();
      const body = { media_type: stage === "media" ? media.media_type : "text", text: stage === "text" ? joinedText : "", bg, scheduled_at: schedIso, stickers, drawing_url };
      if (linkEmbed) body.embed = linkEmbed;
      if (stage === "text") {
        body.layout = "free";
      } else {
        const m = await prepareStoryMediaUrl({ media, config, t, ...trimOpts, setProc });
        body.media_url = m.url;
        if (m.poster) body.poster_url = m.poster;
        Object.assign(body, trimPayload(m));
        body.media_transform = transform;
      }
      await api.put(`/churches/${cid}/scheduled-announcements/${editStory.id}`, body);
      toast.success(t("church.announcement_updated", "Annonce programmée mise à jour ✓"));
      onOpenChange(false);
    } catch (e) { setProc(null); toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };

  const frameWidth = frameRef.current?.getBoundingClientRect().width || window.innerWidth;
  const idle = !tool || ["stickers", "music", "trim", "more", "schedule", "share", "hidden", "close_friends", "church", "close", "location", "mention", "link", "templates", "save_template"].includes(tool);
  const showTemplates = tool === "templates" || (!tool && stage === "text" && !hasContent && !editMode);
  const mediaSrc = media ? (media.localUrl || resolveMediaUrl(media.url)) : null;

  return (
    <div ref={frameRef} className="fixed inset-0 z-[80] bg-black select-none overflow-hidden" style={{ containerType: "inline-size" }} data-testid="story-fs-editor" data-stage={stage} data-tool={tool || ""} data-edit={editMode ? 1 : 0}>
      <StoryMediaLayer hasMedia={stage === "media" && !!media} transform={transform} setTransform={setTransform} frameRef={frameRef} onBackgroundTap={() => setSelected(null)} disabled={tool === "draw" || tool === "text"}>
        {stage === "text" ? (
          <div className="absolute inset-0" style={bgStyleOf(bg, gradientCss)} data-testid="story-fs-bg">
            {bg.startsWith("/verse-bg/") && <div className="absolute inset-0 bg-black/25" aria-hidden />}
            {!hasContent && !tool && (
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <span className="text-white/70 text-lg font-semibold" style={{ textShadow: "0 2px 8px rgba(0,0,0,.4)" }} data-testid="story-fs-empty-hint">{t("story.tap_to_type", "Touche pour écrire")}</span>
              </div>
            )}
            {!hasContent && !tool && <button type="button" onClick={() => { setEditingText(null); setTool("text"); }} className="absolute inset-x-0 top-[25%] bottom-[30%]" aria-label={t("story.tap_to_type", "Touche pour écrire")} data-testid="story-fs-tap-to-type" />}
          </div>
        ) : media && (
          <>
            <StoryBlurBackdrop src={storyBlurSrc(media, mediaSrc)} testId="story-fs-blur" />
            {media.media_type === "video"
              ? <video ref={videoRef} src={mediaSrc} poster={NEUTRAL_POSTER} autoPlay muted loop playsInline onLoadedMetadata={onPreviewMeta} onTimeUpdate={onPreviewTime} className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={mediaTransformStyle(transform)} data-testid="story-fs-video" />
              : <img src={mediaSrc} alt="" draggable={false} className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={mediaTransformStyle(transform)} data-testid="story-fs-image" />}
          </>
        )}
      </StoryMediaLayer>

      <StoryDrawLayer ref={drawRef} strokes={strokes} active={tool === "draw"} tool={draw.tool} color={draw.color} thickness={draw.thickness} onCommit={(s) => setStrokes((arr) => [...arr, s])} frameRef={frameRef} baseUrl={baseDrawing} />
      {/* Lot 664 — zone sûre de la visionneuse (en-tête nom/photo · barre « Répondre ») : repères visibles pendant un glisser, bornes du glisser */}
      <div ref={safeTopRef} className={`absolute inset-x-0 top-0 pointer-events-none transition-opacity ${drag.dragging ? "opacity-100" : "opacity-0"}`} style={{ height: `calc(var(--sa-top, 0px) + ${VIEWER_SAFE_TOP})`, background: "rgba(0,0,0,0.28)", borderBottom: "1px dashed rgba(255,255,255,0.45)" }} data-testid="story-fs-safe-top" />
      <div ref={safeBottomRef} className={`absolute inset-x-0 bottom-0 pointer-events-none transition-opacity ${drag.dragging ? "opacity-100" : "opacity-0"}`} style={{ height: `calc(var(--sa-bottom, 0px) + ${VIEWER_SAFE_BOTTOM})`, background: "rgba(0,0,0,0.28)", borderTop: "1px dashed rgba(255,255,255,0.45)" }} data-testid="story-fs-safe-bottom" />
      <StoryStickerLayer stickers={stickers} setStickers={setStickers} selected={selected} setSelected={setSelected} frameRef={frameRef} trashRef={trashRef} safeRefs={safeRefs} onEditText={(s) => { setEditingText(s); setTool("text"); }} onRemove={(s) => { if (s.type === "mention" && s.user_id) setMentions((arr) => arr.filter((m) => m.id !== s.user_id)); if (s.type === "link") dismissLink(); }} onDragState={setDrag} disabled={tool === "draw" || tool === "text"} embed={linkEmbed} t={t} />

      {stage === "media" && media && <UploadOverlay att={media.status === "local" ? null : media} t={t} onRetry={() => {}} />}
      {proc && (
        <div className="absolute inset-0 z-[60] bg-black/60 grid place-items-center" data-testid="story-fs-proc-overlay">
          <div className="w-2/3 max-w-[200px] text-center">
            <div className="h-1.5 rounded-full bg-white/30 overflow-hidden"><div className="h-full bg-white rounded-full transition-[width] duration-200" style={{ width: `${proc.pct}%` }} /></div>
            <div className="text-white text-xs mt-2 font-medium">{(proc.phase === "trim" ? t("story.trimming", "Découpage") : t("story.uploading_clip", "Envoi")) + ` ${proc.pct}%`}</div>
          </div>
        </div>
      )}

      <TrashZone ref={trashRef} visible={drag.dragging} hot={drag.overTrash} label={t("story.trash_hint", "Relâche ici pour supprimer")} />

      {idle && !drag.dragging && (
        <>
          <StoryTopBar onClose={requestClose} onTool={(k) => { setSelected(null); if (k === "text") setEditingText(null); setTool(k); }} hasMusic={!!music} hasMedia={stage === "media" && !!media} hasFx={hasFx(transform.fx)} onPreview={hasContent ? openPreview : null} t={t} />
          <StoryChipRow stage={stage} bg={bg} setBg={setBg} onFiles={handleFiles} onCamera={editMode ? null : () => { setSelected(null); setTool("camera"); }} canTrim={canTrim} onTrim={() => setTool("trim")} onTemplates={() => setTool(tool === "templates" ? null : "templates")} templatesOn={tool === "templates"} t={t} />
          {showTemplates && <StoryTemplatesRow onPick={applyTemplate} onPickChurchService={applyChurchService} onPickChurchTemplate={applyChurchTemplate} onDeleteChurchTemplate={deleteChurchTemplate} churchSections={churchSections} onClose={tool === "templates" ? closeTool : null} t={t} />}
          <StoryBottomBar user={user} churches={churches} onMyStory={() => publishTo("me")} onChurch={onChurchBtn} onShare={() => setTool("share")} onMore={() => setTool("more")} busy={busy} scheduled={!!scheduledAt} editMode={editMode} scheduledAt={scheduledAt} onSchedule={() => setTool("schedule")} onSave={saveEdit} audience={audience} lang={lang} t={t} />
        </>
      )}
      {tool === "draw" && <StoryDrawTools draw={draw} setDraw={setDraw} canUndo={strokes.length > 0 || !!baseDrawing} onUndo={() => { if (strokes.length) setStrokes((arr) => arr.slice(0, -1)); else setBaseDrawing(null); }} onDone={closeTool} t={t} />}
      <StoryCamera open={tool === "camera"} maxSec={maxSec} onCapture={onCameraCapture} onFiles={(files) => { closeTool(); handleFiles(files); }} onText={() => { closeTool(); if (stage === "media") removeMedia(); setEditingText(null); setTool("text"); }} onClose={closeTool} t={t} />
      {tool === "fx" && stage === "media" && media && (
        <MediaFxEditor open media={{ kind: media.media_type, src: mediaSrc, file: media.file || null, poster: media.posterLocal || (media.poster_url ? resolveMediaUrl(media.poster_url) : null) }} initialFx={transform.fx} onApply={applyFx} onClose={closeTool} t={t} />
      )}
      {tool === "text" && <StoryTextTool key={editingText?.id || "new"} initial={editingText} frameWidth={frameWidth} onDone={onTextDone} onCancel={closeTool} t={t} />}

      <StoryStickerSheet open={tool === "stickers"} onClose={closeTool} onAdd={(st) => { if (st) addSticker(st); }} onPickLocation={() => setTool("location")} onPickMention={() => setTool("mention")} onPickLink={() => setTool("link")} hasLink={!!linkEmbed} churches={churches} churchId={elementsChurchId || (dest && dest !== "me" ? dest : null) || (editMode ? (editStory.church_id || initialChurchId) : null)} onChurchChange={setElementsChurchId} t={t} lang={lang} />
      <LocationSheet open={tool === "location"} onClose={closeTool} defaultCity={user?.city || ""} onPick={(city) => addSticker({ type: "location", text: city, scale: 0.05 })} t={t} />
      <LinkSheet open={tool === "link"} onClose={closeTool} initial={manualLink || linkEmbed?.url || ""} onPick={addManualLink} t={t} />
      <MentionSheet open={tool === "mention"} onClose={closeTool} onPick={addMention} exclude={mentions.map((m) => m.id)} t={t} />
      <BottomSheet open={tool === "music"} onClose={closeTool} title={t("story.music", "Musique")} light testId="story-fs-music-sheet" maxH="80dvh">
        <MusicPicker music={music} onChange={setMusic} previewVolume={musicVolume} t={t} testId="story-fs-music" />
        {music && (
          <>
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-xs font-medium flex items-center gap-1.5"><Volume2 size={14} style={{ color: "var(--yalola-primary)" }} /> {t("composer.music_volume", "Volume de la musique")}</span>
              <div className="flex gap-1" data-testid="story-fs-music-volume">
                {[["soft", 0.35, "Doux"], ["medium", 0.7, "Moyen"], ["loud", 1.0, "Fort"]].map(([key, val, label]) => {
                  const on = Math.abs(musicVolume - val) < 0.01;
                  return <button key={key} type="button" onClick={() => setMusicVolume(val)} className="rounded-full px-2.5 py-1 text-xs font-semibold border" style={{ borderColor: on ? "var(--yalola-primary)" : "var(--yalola-border)", background: on ? "var(--yalola-primary)" : "transparent", color: on ? "#fff" : "inherit" }} data-testid={`story-fs-music-vol-${key}`}>{t(`composer.music_vol_${key}`, label)}</button>;
                })}
              </div>
            </div>
            {/* Étape suivante explicite : le son choisi est gardé, la feuille se ferme, retour à la story. */}
            <button type="button" onClick={closeTool} className="mt-3 w-full h-11 rounded-full text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" style={{ background: "var(--yalola-primary)" }} data-testid="story-fs-music-done">
              <Check size={16} /> {t("story.music_use", "Utiliser ce son")} · ♪ {music.title || t("story.music", "Musique")}
            </button>
          </>
        )}
      </BottomSheet>
      <StoryTrimSheet open={tool === "trim"} onClose={closeTool} videoDur={videoDur} maxSec={maxSec} trimStart={trimStart} trimLen={trimLen} setTrim={setTrim} clipQuality={clipQuality} setClipQuality={setClipQuality} willProcess={willProcess} thumbsSrc={config?.settings?.features?.story_trim_thumbnails !== false ? media?.localUrl || null : null} t={t} />
      <MoreSheet
        open={tool === "more"} onClose={closeTool} hasMedia={stage === "media" && !!media} scheduledAt={scheduledAt}
        onSchedule={() => setTool("schedule")} onRemoveMedia={removeMedia} onDiscard={() => { if (!editMode) localStorage.removeItem(DRAFT_KEY_FS); onOpenChange(false); }} editMode={editMode} lang={lang}
        onSaveTemplate={churches.length > 0 && hasContent ? () => setTool("save_template") : null}
        galleryNode={<GalleryRow onFiles={handleFiles} t={t} />}
        t={t}
      />
      <ScheduleSheet open={tool === "schedule"} onClose={closeTool} scheduledAt={scheduledAt} setScheduledAt={setScheduledAt} t={t} />
      <ChurchPickSheet open={tool === "church"} onClose={closeTool} churches={churches} onPick={(cid) => { closeTool(); publishTo(cid); }} t={t} />
      <StoryShareSheet open={tool === "share"} onClose={closeTool} user={user} churches={churches} dest={dest} setDest={setDest} mandatory={mandatory} audience={audience} setAudience={setAudience} onHiddenFrom={() => setTool("hidden")} onCloseFriends={() => setTool("close_friends")} mentions={mentions} setMentions={setMentions} scheduledAt={scheduledAt} setScheduledAt={setScheduledAt} onShare={(d) => publishTo(d || "me")} onPreview={hasContent ? openPreview : null} busy={busy} t={t} />
      <StoryHiddenFromSheet open={tool === "hidden"} onClose={() => setTool("share")} t={t} />
      <CloseFriendsSheet open={tool === "close_friends"} onClose={() => setTool("share")} t={t} />
      <SaveTemplateSheet open={tool === "save_template"} onClose={closeTool} churches={churches} defaultChurchId={(dest && dest !== "me" ? dest : null) || (editMode ? (editStory.church_id || initialChurchId) : null)} onSave={saveAsTemplate} busy={busy} t={t} />
      {tool === "preview" && (
        <StoryPreview
          author={previewAuthor} stage={stage} media={media} mediaSrc={mediaSrc} transform={transform} bg={bg} stickers={stickers}
          drawingUrl={previewDrawing || (strokes.length ? null : baseDrawing)} linkEmbed={linkEmbed} music={music} musicVolume={musicVolume} mentions={mentions}
          editMode={editMode} busy={busy} onEdit={closeTool} onShare={shareFromPreview} t={t}
        />
      )}
      <CloseSheet open={tool === "close"} onClose={closeTool} onKeep={() => onOpenChange(false)} onDiscard={() => { if (!editMode) localStorage.removeItem(DRAFT_KEY_FS); onOpenChange(false); }} editMode={editMode} lang={lang} t={t} />
    </div>
  );
};
